import React, { Suspense } from 'react';
import { LoadingOverlay } from '../common/loading/LoadingOverlay';
import { useErrorContext } from '../error/ErrorBoundaryContext';

type Props = {
  children: React.ReactNode;
  promiseToWait?: Promise<unknown>;
  isLoading?: boolean;
};

const getComponentLazyWithPromise = (
  promise: Promise<unknown>,
  children: React.ReactNode,
  onError: (error: any) => void
) =>
  React.lazy(async () => {
    try {
      await promise;
    } catch (e) {
      onError(e);
    }

    return {
      default: () => children,
    };
  });

const LoadingFallback = () => <LoadingOverlay isVisible={true} />;

export const SuspenseWrapper = ({
  children,
  promiseToWait,
  isLoading,
}: Props) => {
  const { notifyErrorFromComponent } = useErrorContext();

  if (isLoading) {
    return <LoadingFallback />;
  }

  const ComponentLazy = promiseToWait
    ? getComponentLazyWithPromise(
        promiseToWait,
        children,
        notifyErrorFromComponent
      )
    : () => children;

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ComponentLazy />
    </Suspense>
  );
};
